import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { Divider } from 'primeng/divider';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { ILoggerNoiseTableData } from '@app/main/leak-detection/interfaces/logger-noise-table-data';
import { LeakDetectionTableLoggerNoiseDataService } from '@app/main/leak-detection/services/leak-detection-table-logger-noise/leak-detection-table-logger-noise-data.service';
import { LdLoggerNoiseLeakScoreGaugeComponent } from '@app/main/leak-detection/components/ld-helper/ld-logger-noise-leak-score-gauge/ld-logger-noise-leak-score-gauge.component';
import { getLoggerMarkerIcon, LoggerMarkerIconProperty } from '@app/main/main-content/map/configs/logger-marker-icon';
import { MarkerAbbreviation } from '@app/main/main-content/map/enums/marker-abbreviation.enum';
import { LoggerMarkerMode } from '@app/main/main-content/map/enums/logger-marker-mode.enum';
import { LeakDetectionStatus } from '@app/core/enums/leak-detection-status.enum';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { GridLinesDatatableToken } from 'src/theme/components/datatable/grid-lines-datatable-token';
import { ILocalizationKey } from '@app/core/interfaces/localization';
import { ReportCoordinatesOption } from '@app/core/enums/report-coordinates-option.enum';
import { LD_REPORT_COORDINATES_OPTIONS } from '@app/main/leak-detection/configs/ld-report-coordinates.config';
import { reportCoordinatesLocalize } from '@app/core/localize/report-coordinates';
import { LOGGER_NOISE_REPORT_PARAMETERS_CHECKBOX_OPTIONS } from '@app/main/leak-detection/dialogs/dialog-create-logger-noise-report/dialog-create-logger-noise-report.config';
import { LoggerNoiseReportParametersControlKey } from '@app/main/leak-detection/enums/logger-noise-report-parameters-control-key.enum';
import { ReportUrlService } from '@app/main/main-screen/services/report-url/report-url.service';
import { LdReportType } from '@app/main/leak-detection/enums/ld-report-type.enum';
import { ReportRowSelectionState, ReportTableSortState } from '@app/main/leak-detection/utils/report-table-state';
import { SortOrder } from '@app/core/enums/sort-order.enum';

enum LoggerNoiseReportTab {
  Loggers = 'loggers',
  Parameters = 'parameters',
}

type LoggerNoiseSortField = 'predictionScore' | 'logger';

interface ILoggerNoiseReportParametersForm extends Record<LoggerNoiseReportParametersControlKey, FormControl<boolean>> {
  coordinates: FormControl<ReportCoordinatesOption>;
  additionalInformation: FormControl<string>;
}

export interface IDialogCreateLoggerNoiseReportData {
  loggerIds?: string[];
}

@Component({
  selector: 'gc-dialog-create-logger-noise-report',
  templateUrl: './dialog-create-logger-noise-report.component.html',
  styleUrl: './dialog-create-logger-noise-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgTemplateOutlet,
    Button,
    Checkbox,
    TableModule,
    SelectModule,
    TextareaModule,
    Divider,
    FormsModule,
    ReactiveFormsModule,
    SvgIconComponent,
    TranslateDirective,
    TranslatePipe,
    LdLoggerNoiseLeakScoreGaugeComponent,
  ],
})
export class DialogCreateLoggerNoiseReportComponent {
  private ref = inject(DynamicDialogRef);
  private config = inject<DynamicDialogConfig<IDialogCreateLoggerNoiseReportData>>(DynamicDialogConfig);
  private dataService = inject(LeakDetectionTableLoggerNoiseDataService);
  private reportUrlService = inject(ReportUrlService);
  protected readonly formattingData = inject(FormattingDataService);

  public readonly gridLinesDatatableToken = GridLinesDatatableToken;
  protected readonly LoggerNoiseReportTab = LoggerNoiseReportTab;
  protected readonly SortOrder = SortOrder;

  public readonly parametersCheckboxOptions = LOGGER_NOISE_REPORT_PARAMETERS_CHECKBOX_OPTIONS;
  public readonly coordinatesOptions = LD_REPORT_COORDINATES_OPTIONS;
  public readonly coordinatesLabels = reportCoordinatesLocalize;

  public parametersForm = new FormGroup<ILoggerNoiseReportParametersForm>({
    [LoggerNoiseReportParametersControlKey.Map]: new FormControl(true, { nonNullable: true }),
    [LoggerNoiseReportParametersControlKey.StreetView]: new FormControl(true, { nonNullable: true }),
    [LoggerNoiseReportParametersControlKey.Histogram]: new FormControl(true, { nonNullable: true }),
    [LoggerNoiseReportParametersControlKey.SignalSpectrum]: new FormControl(true, { nonNullable: true }),
    [LoggerNoiseReportParametersControlKey.LeakScoreHistory]: new FormControl(true, { nonNullable: true }),
    [LoggerNoiseReportParametersControlKey.NoiseLevelHistory]: new FormControl(true, { nonNullable: true }),
    coordinates: new FormControl(ReportCoordinatesOption.None, { nonNullable: true }),
    additionalInformation: new FormControl('', { nonNullable: true }),
  });

  public activeTab = signal<LoggerNoiseReportTab>(LoggerNoiseReportTab.Loggers);

  public loggers = signal<ILoggerNoiseTableData[]>([]);

  private tableSort = new ReportTableSortState<ILoggerNoiseTableData, LoggerNoiseSortField>(
    'predictionScore',
    SortOrder.Desc,
    (row, field) => {
      switch (field) {
        case 'predictionScore':
          return row.predictionScore ?? 0;
        case 'logger':
          return row.serialNo ?? '';
      }
    },
    {
      predictionScore: ['logger'],
    }
  );
  public sortField = this.tableSort.sortField;
  public sortOrder = this.tableSort.sortOrder;
  public sortedLoggers = computed(() => this.tableSort.sort(this.loggers()));

  private rowSelection = new ReportRowSelectionState<ILoggerNoiseTableData>(() => this.loggers());
  public selectedIds = this.rowSelection.selectedIds;
  public totalCount = this.rowSelection.totalCount;
  public selectedCount = this.rowSelection.selectedCount;
  public allSelected = this.rowSelection.allSelected;
  public partiallySelected = this.rowSelection.partiallySelected;

  constructor() {
    this.dataService
      .getData()
      .pipe(take(1), takeUntilDestroyed())
      .subscribe(rows => {
        this.loggers.set(rows);

        const preselectedIds = (this.config.data?.loggerIds ?? []).filter(id => rows.some(row => row.id === id));

        if (preselectedIds.length) {
          this.rowSelection.selectRows(preselectedIds);
        } else {
          this.rowSelection.selectAllRows();
        }
      });
  }

  public setActiveTab(tab: LoggerNoiseReportTab): void {
    this.activeTab.set(tab);
  }

  public toggleSort(field: LoggerNoiseSortField): void {
    this.tableSort.toggleSort(field);
  }

  public toggleAll(): void {
    this.rowSelection.toggleAll();
  }

  public toggleRow(id: string): void {
    this.rowSelection.toggleRow(id);
  }

  public loggerIcon(row: ILoggerNoiseTableData): { src: string; name: string | undefined } {
    const mode = row.raw.deployment.isPositionTemporary ? LoggerMarkerMode.PositionTemporary : LoggerMarkerMode.Default;
    const status = row.predictionStatus ?? LeakDetectionStatus.Neutral;

    return {
      src: getLoggerMarkerIcon(mode, row.loggerType, status, MarkerAbbreviation.True, LoggerMarkerIconProperty.Src),
      name: getLoggerMarkerIcon(mode, row.loggerType, status, MarkerAbbreviation.True, LoggerMarkerIconProperty.Name),
    };
  }

  public coordinatesLabel(value: ReportCoordinatesOption): ILocalizationKey {
    return this.coordinatesLabels[value];
  }

  public isPreviewDisabled(): boolean {
    return (
      this.selectedCount() === 0 ||
      this.parametersCheckboxOptions.every(option => !this.parametersForm.controls[option.controlKey].value)
    );
  }

  public cancel(): void {
    this.ref.close();
  }

  public preview(): void {
    const value = this.parametersForm.getRawValue();

    this.reportUrlService
      .openWithCurrentSelection({
        type: LdReportType.Measurement,
        additionalInfo: value.additionalInformation,
        coordinateFormat: value.coordinates,
        ids: this.selectedReportIds().join(','),
        includeMap: value.map,
        includeStreetView: value.streetView,
        includeHistogram: value.histogram,
        includeSignalSpectrum: value.signalSpectrum,
        includeLeakScoreHistory: value.leakScoreHistory,
        includeDbMinHistory: value.noiseLevelHistory,
      })
      .subscribe(opened => {
        if (opened) this.ref.close();
      });
  }

  private selectedReportIds(): string[] {
    const selectedIds = this.selectedIds();

    return this.loggers()
      .filter(row => selectedIds[row.id])
      .map(({ raw }) => (raw.measurement ? `MEAS-${raw.measurement.id}` : `DEPL-${raw.deployment.id}`));
  }
}
